import type { OrderSummary } from "@smeetz/orders";

/**
 * FIS-719 — open-balance rules the POS adds on top of the engine's figures
 * (`OrderSummary.amountDue`, `openBalance`, `openToPay`, read directly). Kept
 * free of Vue/Pinia/store imports so it stays testable in isolation.
 */

// Same rounding as the order engine (`round2` in @smeetz/orders).
const round2 = (value: number): number => Math.round(value * 100) / 100;

/**
 * What the active session itself still owes (its lines minus its payments),
 * i.e. `amountDue` without the open debt. This is the part "Complete with
 * balance" defers; when it is 0 there is nothing to defer.
 */
export function activeSessionBalanceOf(summary: OrderSummary): number {
  return round2(summary.amountDue - summary.openBalance);
}

/**
 * Debt left open by visits OTHER than the active one: the open debts
 * (`summary.openToPay`) carried by another session, clamped at 0. A debt
 * deferred on the active session itself (a retry after "Complete with
 * balance" deferred but the completion failed) is not a previous visit's.
 */
export function previousVisitsOpenBalanceOf(
  summary: OrderSummary,
  activeSessionId: string | undefined
): number {
  const open = summary.openToPay
    .filter((entry) => entry.orderSessionId !== activeSessionId)
    .reduce((sum, entry) => sum + entry.remaining, 0);
  return Math.max(0, round2(open));
}

/**
 * Visibility of the "Complete with balance" button: shown while the amount due
 * is not 0 on a session the cashier has actually worked on, as on the old POS.
 * Positive: money to collect now, which the completion defers. Negative: money
 * owed back (Refund step); the completion closes the visit without refunding,
 * since the engine only refuses to complete a positive balance. Gating on the
 * amount due — not on the session's own balance — also lets the cashier close
 * a visit that only paid part of an older debt, a cancellation netted against
 * debt, or a retry after a deferral succeeded but the completion failed
 * (`completeOrderWithBalance` defers only when the session itself owes money).
 * French tills never show it: NF525 forbids closing a sale while a balance
 * remains (L3-8342).
 */
export function shouldShowCompleteWithBalance({
  settingOn,
  isFrench,
  amountDue,
  sessionHasContent,
}: {
  /** The POS "complete booking with balance" device setting (no feature flag). */
  settingOn: boolean;
  isFrench: boolean;
  /** `OrderSummary.amountDue` */
  amountDue: number;
  /** `isNonEmptyOrderSession(activeSession)` */
  sessionHasContent: boolean;
}): boolean {
  return settingOn && !isFrench && amountDue !== 0 && sessionHasContent;
}
